import React, { useEffect, useMemo, useState } from "react";
import ParametricDotsLibrary from "../../libraries/ParametricDotsLibrary";
import useParameterStore from "../../store/parameterStore";

const getParamNumericValue = (param) => {
  if (!param) return 0;

  const raw = param.defaultVal ?? param.value ?? param.defaultValue ?? 0;
  const n = Number(raw);

  return Number.isFinite(n) ? n : 0;
};

const getConnValue = (conn, axis) => {
  const key = axis === "y" ? "spacingY" : "spacingX";
  const value = conn[key];

  if (value === "" || value === null || value === undefined) return "";
  return String(value);
};

const ConnectionsTab = () => {
  const [connections, setConnections] = useState(
    ParametricDotsLibrary.getConnections()
  );
  const [selectedConnectionName, setSelectedConnectionName] = useState("");
  const globalParams = useParameterStore((state) => state.globalParameters);
  const [draftValues, setDraftValues] = useState({});

  useEffect(() => {
    const handler = (e) => {
      const nextConnections = e.detail?.connections || [];
      setConnections(nextConnections);

      setDraftValues((prev) => {
        const next = { ...prev };

        for (const conn of nextConnections) {
          const xKey = `${conn.name}:x`;
          const yKey = `${conn.name}:y`;

          if (!(xKey in next)) next[xKey] = getConnValue(conn, "x");
          if (!(yKey in next)) next[yKey] = getConnValue(conn, "y");
        }

        return next;
      });
    };

    window.addEventListener("parametric-connections-changed", handler);

    return () => {
      window.removeEventListener("parametric-connections-changed", handler);
    };
  }, []);

  useEffect(() => {
    setDraftValues((prev) => {
      const next = { ...prev };

      for (const conn of connections) {
        const xKey = `${conn.name}:x`;
        const yKey = `${conn.name}:y`;

        if (!(xKey in next)) next[xKey] = getConnValue(conn, "x");
        if (!(yKey in next)) next[yKey] = getConnValue(conn, "y");
      }

      return next;
    });
  }, [connections]);

  const numericGlobalParams = useMemo(() => {
    return globalParams.filter((p) => {
      const type = String(p.type || "").toLowerCase();
      return ["i", "f", "int", "float", "number"].includes(type);
    });
  }, [globalParams]);

  const handleSelectConnection = (name) => {
    setSelectedConnectionName(name);

    const ok = ParametricDotsLibrary.selectConnectionDots(
      window.canvasEngine,
      name
    );

    if (!ok) {
      console.warn("⚠️ Could not highlight selected connection dots:", name);
    }
  };

  const syncConnections = () => {
    setConnections([...ParametricDotsLibrary.getConnections()]);
  };

  const applySpacing = (conn, axis, rawValue, paramName = "") => {
    const cleanValue = rawValue === "" ? 0 : rawValue;

    ParametricDotsLibrary.setConnectionSpacing(
      window.canvasEngine,
      conn.name,
      axis,
      cleanValue,
      paramName
    );

    syncConnections();
  };

  const handleManualChange = (conn, axis, value) => {
    const key = `${conn.name}:${axis}`;

    setDraftValues((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (value === "") return;

    const n = Number(value);
    if (!Number.isFinite(n)) return;

    applySpacing(conn, axis, n, "");
  };

  const handleParamSelect = (conn, axis, paramName) => {
    const key = `${conn.name}:${axis}`;

    if (!paramName) {
      setDraftValues((prev) => ({
        ...prev,
        [key]: getConnValue(conn, axis),
      }));
      return;
    }

    const matched = numericGlobalParams.find(
      (p) => String(p.name) === String(paramName)
    );

    if (!matched) return;

    const value = getParamNumericValue(matched);

    setDraftValues((prev) => ({
      ...prev,
      [key]: String(value),
    }));

    applySpacing(conn, axis, value, matched.name);
  };

  const handleZero = (conn) => {
    ParametricDotsLibrary.setConnectionSpacing(
      window.canvasEngine,
      conn.name,
      "x",
      0,
      ""
    );

    ParametricDotsLibrary.setConnectionSpacing(
      window.canvasEngine,
      conn.name,
      "y",
      0,
      ""
    );

    setDraftValues((prev) => ({
      ...prev,
      [`${conn.name}:x`]: "0",
      [`${conn.name}:y`]: "0",
    }));

    syncConnections();
  };

  const handleDisconnect = (name) => {
    ParametricDotsLibrary.disconnectByName(window.canvasEngine, name);

    if (selectedConnectionName === name) {
      setSelectedConnectionName("");
      ParametricDotsLibrary.clearSelectedConnectionDots(window.canvasEngine);
    }

    syncConnections();
  };

  if (!connections.length) {
    return (
      <div style={{ padding: "12px", color: "#eee", fontSize: "13px" }}>
        <div style={emptyStateStyle}>No connections yet.</div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      {connections.map((conn) => {
        const xKey = `${conn.name}:x`;
        const yKey = `${conn.name}:y`;

        return (
          <div
            key={conn.name}
            onClick={() => handleSelectConnection(conn.name)}
            style={{
              ...cardStyle,
              ...(selectedConnectionName === conn.name
                ? selectedCardStyle
                : {}),
            }}
          >
            <div style={cardHeaderStyle}>
              <div>
                <div style={connTitleStyle}>{conn.name}</div>
                <div style={connSubTitleStyle}>
                  {conn.refAName} ({conn.dotALabel}) ↔ {conn.refBName} ({conn.dotBLabel})
                </div>
              </div>

              <div style={badgeStyle}>ACTIVE</div>
            </div>

            <div
              style={spacingSectionStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={fieldRowStyle}>
                <label style={fieldLabelStyle}>Spacing X</label>

                <input
                  type="text"
                  value={draftValues[xKey] ?? getConnValue(conn, "x")}
                  onChange={(e) => handleManualChange(conn, "x", e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      applySpacing(conn, "x", 0, "");
                      setDraftValues((prev) => ({ ...prev, [xKey]: "" }));
                    }
                  }}
                  style={valueInputStyle}
                  placeholder="Value"
                />

                <select
                  value={conn.spacingXParam || ""}
                  onChange={(e) => handleParamSelect(conn, "x", e.target.value)}
                  style={paramSelectStyle}
                >
                  <option value="">Select Param</option>
                  {numericGlobalParams.map((param) => (
                    <option key={`x-${conn.name}-${param.name}`} value={param.name}>
                      {param.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={fieldRowStyle}>
                <label style={fieldLabelStyle}>Spacing Y</label>

                <input
                  type="text"
                  value={draftValues[yKey] ?? getConnValue(conn, "y")}
                  onChange={(e) => handleManualChange(conn, "y", e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      applySpacing(conn, "y", 0, "");
                      setDraftValues((prev) => ({ ...prev, [yKey]: "" }));
                    }
                  }}
                  style={valueInputStyle}
                  placeholder="Value"
                />

                <select
                  value={conn.spacingYParam || ""}
                  onChange={(e) => handleParamSelect(conn, "y", e.target.value)}
                  style={paramSelectStyle}
                >
                  <option value="">Select Param</option>
                  {numericGlobalParams.map((param) => (
                    <option key={`y-${conn.name}-${param.name}`} value={param.name}>
                      {param.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={actionRowStyle}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZero(conn);
                  }}
                  style={zeroBtnStyle}
                >
                  ZERO
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDisconnect(conn.name);
                  }}
                  style={disconnectBtnStyle}
                >
                  DISCONNECT
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const wrapperStyle = {
  padding: "10px",
  color: "#eee",
  fontSize: "13px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const emptyStateStyle = {
  padding: "12px",
  border: "1px solid #444",
  borderRadius: "8px",
  background: "#1a1a1a",
  color: "#bbb",
};

const cardStyle = {
  border: "1px solid #343434",
  borderRadius: "12px",
  padding: "12px",
  background:
    "linear-gradient(180deg, rgba(27,27,27,0.98) 0%, rgba(20,20,20,0.98) 100%)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "0 4px 14px rgba(0,0,0,0.22)",
};

const selectedCardStyle = {
  border: "1px solid #00d8ff",
  boxShadow: "0 0 0 1px rgba(0,216,255,0.2), 0 8px 22px rgba(0,216,255,0.12)",
  background:
    "linear-gradient(180deg, rgba(9,39,47,0.95) 0%, rgba(17,24,27,0.98) 100%)",
};

const cardHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "8px",
  marginBottom: "12px",
};

const connTitleStyle = {
  fontSize: "18px",
  fontWeight: "700",
  color: "#00e5ff",
  lineHeight: 1.1,
};

const connSubTitleStyle = {
  marginTop: "4px",
  fontSize: "13px",
  color: "#d0d0d0",
  lineHeight: 1.4,
};

const badgeStyle = {
  fontSize: "10px",
  fontWeight: "700",
  color: "#00e5ff",
  border: "1px solid rgba(0,229,255,0.3)",
  background: "rgba(0,229,255,0.08)",
  padding: "4px 8px",
  borderRadius: "999px",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
};

const spacingSectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const fieldRowStyle = {
  display: "grid",
  gridTemplateColumns: "82px 72px 1fr",
  gap: "8px",
  alignItems: "center",
};

const fieldLabelStyle = {
  fontSize: "12px",
  fontWeight: "600",
  color: "#b8c2cc",
};

const valueInputStyle = {
  width: "100%",
  height: "34px",
  padding: "6px 8px",
  backgroundColor: "#0f1115",
  border: "1px solid #3d4652",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
};

const paramSelectStyle = {
  width: "100%",
  height: "34px",
  padding: "6px 8px",
  backgroundColor: "#0f1115",
  border: "1px solid #3d4652",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
};

const actionRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px",
  marginTop: "4px",
};

const zeroBtnStyle = {
  height: "34px",
  minWidth: "80px",
  padding: "0 14px",
  background: "linear-gradient(180deg, #2d2d2d 0%, #202020 100%)",
  border: "1px solid #4a4a4a",
  borderRadius: "8px",
  color: "#fff",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "700",
};

const disconnectBtnStyle = {
  height: "34px",
  minWidth: "110px",
  padding: "0 16px",
  background: "linear-gradient(180deg, #3a2a2a 0%, #241818 100%)",
  border: "1px solid #6a3c3c",
  borderRadius: "8px",
  color: "#fff",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "700",
};

export default ConnectionsTab;
